import { createTheme } from '@mui/material/styles';

export const theme = createTheme({
  spacing: 1,
  typography: {
    h3: {
      fontSize: '28px',
      fontWeight: '700',
      lineHeight: 'normal',
      letterSpacing: 'normal',
    },
    h4: {
      fontSize: '24px',
      fontWeight: '700',
      lineHeight: 'normal',
      letterSpacing: 'normal',
    },
    h5: {
      fontSize: '20px',
      fontWeight: '700',
      lineHeight: 'normal',
      letterSpacing: 'normal',
    },
    h6: {
      fontSize: '18px',
      fontWeight: '700',
      lineHeight: 'normal',
      letterSpacing: 'normal',
    },
    subtitle1: {
      fontSize: '16px',
      fontWeight: '400',
      lineHeight: 'normal',
      letterSpacing: 'normal',
    },
    body1: {
      fontSize: '14px',
      fontWeight: '400',
      lineHeight: 'normal',
      letterSpacing: 'normal',
    },
    subtitle2: {
      fontSize: '12px',
      fontWeight: '400',
      lineHeight: '20px',
      letterSpacing: 'normal',
    },
    caption: {
      fontSize: '10px',
      fontWeight: '400',
      lineHeight: 'normal',
      letterSpacing: 'normal',
    },
  },
  palette: {
    primary: {
      main: 'rgb(var(--main-1-color-rgb))',
      dark: 'rgb(var(--main-1-color-rgb))',
      contrastText: 'rgb(var(--white-color-rgb))',
    },
    secondary: {
      main: 'rgb(var(--gray-3-color-rgb))',
      dark: 'rgb(var(--gray-3-color-rgb))',
      contrastText: 'rgb(var(--main-1-color-rgb))',
    },
    text: {
      primary: 'rgb(var(--text-1-color-rgb))',
    },
    error: {
      main: 'rgb(var(--error-color-rgb))',
    },
  },
  components: {
    MuiTextField: {
      defaultProps: {
        fullWidth: true,
      },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root.Mui-disabled': {
            color: 'rgb(var(--text-2-color-rgb))',
            '& input': {
              WebkitTextFillColor: 'rgb(var(--text-2-color-rgb))',
            },
            '& textarea': {
              WebkitTextFillColor: 'rgb(var(--text-2-color-rgb))',
            },
            '& fieldset': {
              borderColor: 'rgba(var(--gray-8-color-rgb), 0.12)',
            },
          },
        },
      },
    },
    MuiPickersTextField: {
      defaultProps: {
        fullWidth: true,
      },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root.Mui-disabled': {
            color: 'rgb(var(--text-2-color-rgb))',
            '& input': {
              WebkitTextFillColor: 'rgb(var(--text-2-color-rgb))',
            },
            '& textarea': {
              WebkitTextFillColor: 'rgb(var(--text-2-color-rgb))',
            },
            '& fieldset': {
              borderColor: 'rgba(var(--gray-8-color-rgb), 0.12)',
            },
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          lineHeight: '16px',
        },
      },
    },
    MuiButton: {
      defaultProps: {
        fullWidth: true,
        size: 'large',
        variant: 'contained',
        loadingPosition: 'start',
      },
      styleOverrides: {
        root: {
          fontSize: '16px',
          fontWeight: '600',
          textTransform: 'none',
          boxShadow: 'none',
          '&:disabled': {
            color: 'rgb(var(--white-color-rgb))',
            backgroundColor: 'rgba(var(--main-1-color-rgb), 0.4)',
          },
        },
        sizeSmall: {
          height: '36px',
          fontSize: '15px',
          fontWeight: '500',
          lineHeight: '16px',
        },
        outlined: {
          border: '1px solid transparent',
          borderRadius: '8px',
        },
        outlinedPrimary: {
          borderColor: 'rgb(var(--main-1-color-rgb))',
          backgroundColor: 'rgba(var(--main-1-color-rgb), 0.05)',
        },
        outlinedSecondary: {
          borderColor: 'rgb(var(--gray-2-color-rgb))',
          backgroundColor: 'rgba(var(--gray-2-color-rgb), 0.05)',
          color: 'rgb(var(--gray-2-color-rgb))',
          '&:hover': {
            borderColor: 'rgb(var(--gray-2-color-rgb))',
            backgroundColor: 'rgba(var(--gray-2-color-rgb), 0.05)',
          },
        },
        outlinedError: {
          borderColor: 'rgb(var(--error-color-rgb))',
          backgroundColor: 'rgba(var(--main-1-color-rgb), 0.05)',
          color: 'rgb(var(--error-color-rgb))',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: '12px',
          backgroundColor: 'rgb(var(--white-color-rgb))',
          border: '1px solid rgb(var(--gray-1-color-rgb))',
          boxShadow: '0px 4px 4px 0px rgba(var(--gray-1-color-rgb), 0.3)',
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(var(--black-color-rgb), 0.3)',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          width: '120px',
          padding: '0px',
          textTransform: 'none',
          lineHeight: '20px',
          color: 'rgb(var(--gray-2-color-rgb))',
          '&:selected': {
            color: 'rgb(var(--text-2-color-rgb))',
          },
          marginRight: '10px',
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgb(var(--gray-6-color-rgb))',
          borderRadius: '10px',
        },
        bar: {
          backgroundColor: 'rgb(var(--main-2-color-rgb))',
          borderRadius: '10px',
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        inputRoot: {
          paddingLeft: '16px',
          paddingRight: '16px !important',
        },
        tag: {
          margin: '2px',
          borderRadius: '8px',
          border: '1px solid rgb(var(--gray-6-color-rgb))',
          backgroundColor: 'transparent',
          fontSize: '14px',
          lineHeight: '20px',
          fontWeight: '500',
          color: 'rgb(var(--gray-7-color-rgb))',
        },
        paper: {
          boxShadow:
            '0px 2px 6px 2px rgba(var(--black-color-rgb), 0.15), 0px 1px 2px 0px rgba(var(--black-color-rgb), 0.3)',
        },
        option: {
          padding: '16px !important',
          fontSize: '16px',
          lineHeight: '20px',
          color: 'rgb(var(--text-2-color-rgb))',
          '&:hover': {
            backgroundColor: 'rgb(var(--gray-4-color-rgb))',
          },
          "&[aria-selected='true']": {
            backgroundColor: 'rgb(var(--gray-4-color-rgb))',
          },
        },
      },
    },
    MuiYearCalendar: {
      styleOverrides: {
        button: {
          textAlign: 'center',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        icon: {
          color: 'rgb(var(--white-color-rgb))',
        },
        message: {
          color: 'rgb(var(--white-color-rgb))',
        },
        action: {
          color: 'rgb(var(--white-color-rgb))',
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          '&.Mui-disabled': {
            color: 'rgba(var(--text-2-color), 0.38)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          '&.Mui-disabled': {
            opacity: '1',
          },
        },
      },
    },
    MuiMenu: {
      defaultProps: {
        BackdropProps: {
          style: { backgroundColor: 'transparent' },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          padding: '9px 33px 9px 15px',
        },
      },
    },
    MuiList: {
      styleOverrides: {
        root: {
          padding: '0px',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: '6px',
          '&:hover': {
            backgroundColor: 'var(--black-color-10)',
          },
          '&.Mui-selected': {
            backgroundColor: 'var(--white-color)',
          },
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          border: '1px solid rgb(var(--gray-6-color-rgb))',
          boxShadow: '0px 4px 4px 0px rgba(var(--black-color-rgb), 0.25)',
        },
      },
      defaultProps: {
        BackdropProps: {
          style: { backgroundColor: 'transparent' },
        },
      },
    },
    MuiDayCalendar: {
      styleOverrides: {
        weekDayLabel: {
          fontSize: '14px',
          lineHeight: '21px',
          color: 'rgb(var(--gray-2-color-rgb))',
        },
      },
    },
    MuiPickersDay: {
      styleOverrides: {
        root: {
          fontSize: '12px',
          lineHeight: '18px',
          color: 'rgb(var(--gray-2-color-rgb))',
        },
      },
    },
    MuiPickersCalendarHeader: {
      styleOverrides: {
        labelContainer: {
          lineHeight: '21px',
          color: 'rgb(var(--gray-2-color-rgb))',
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          borderTop: '1px solid rgb(var(--gray-4-color-rgb))',
          '& > button': {
            borderRadius: '8px',
            padding: '4px 8px',
            fontWeight: '500',
            fontSize: '14px',
            lineHeight: '21px',
          },
          '& > button:first-of-type': {
            backgroundColor: 'rgb(var(--gray-3-color-rgb))',
            color: 'rgb(var(--gray-2-color-rgb))',
          },
          '& > button:last-of-type': {
            backgroundColor: 'rgb(var(--main-2-color-rgb))',
            color: 'rgb(var(--white-color-rgb))',
          },
        },
      },
    },
  },
});
